import { FormFieldConstants } from "utils/constants";
import type { IncomingInvoiceFormTypes } from "models/formTypes";
import { CreditPaymentHandling } from "models/enums/incomingInvoice";
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import PeriodisationRowsHelper from "utils/helpers/periodisationRowsHelper";

// Already paid stops the payment, so only a known choice may set it; any other value keeps the form's own.
const isAlreadyPaidByHandling: Record<CreditPaymentHandling, boolean> = {
  [CreditPaymentHandling.Refund]: true,
  [CreditPaymentHandling.UseAgainstFutureInvoices]: false,
  [CreditPaymentHandling.Other]: true,
};

export const getUpdatedInvoice = (
  invoice: ApiModels.IncomingInvoice,
  values: IncomingInvoiceFormTypes.Details | IncomingInvoiceFormTypes.Todo
) => {
  const isCredit = FormFieldConstants.IncomingInvoice.Details.IsCredit in values ? values.isCredit : invoice.isCredit;
  const hasCreditPaymentHandling = FormFieldConstants.IncomingInvoice.Todo.CreditPaymentHandling in values;
  // The form keeps the Other text after another option is picked, and Core stores text sent with a credit
  // marked already paid and opens a support case from it on approval, so only Other may send it.
  const isOtherHandling = hasCreditPaymentHandling && values.creditPaymentHandling === CreditPaymentHandling.Other;
  // The invoice page's details form has no credit choice, and sending no text there makes Core clear the stored one.
  const paymentHandlingComment = !hasCreditPaymentHandling
    ? invoice.paymentHandlingComment
    : isOtherHandling
      ? values.paymentHandlingInformation
      : undefined;

  let isAlreadyPaid =
    FormFieldConstants.IncomingInvoice.Details.IsAlreadyPaid in values
      ? (values.isAlreadyPaid ?? false)
      : invoice.isAlreadyPaid;

  const handlingIsAlreadyPaid =
    hasCreditPaymentHandling && values.creditPaymentHandling !== undefined
      ? isAlreadyPaidByHandling[values.creditPaymentHandling]
      : undefined;

  if (isCredit && handlingIsAlreadyPaid !== undefined) {
    isAlreadyPaid = handlingIsAlreadyPaid;
  }

  let account =
    FormFieldConstants.IncomingInvoice.Details.Account in values ? (values.account?.id as number) : invoice.account;

  if (account == null && invoice.isAutoInvoice) {
    account = 0;
  }

  const invoiceDate =
    FormFieldConstants.IncomingInvoice.Details.InvoiceDate in values
      ? (values.invoiceDate ?? invoice.invoiceDate)
      : invoice.invoiceDate;

  const updatedInvoice: ApiModels.IncomingInvoice = {
    ...invoice,
    account,
    referencePerson:
      FormFieldConstants.IncomingInvoice.Details.ReferencePerson in values
        ? values.referencePerson
        : invoice.referencePerson,
    dueDate:
      FormFieldConstants.IncomingInvoice.Details.DueDate in values ? (values.dueDate ?? undefined) : invoice.dueDate,
    invoiceDate,
    toPayDate:
      FormFieldConstants.IncomingInvoice.Details.PaymentDate in values
        ? (values.paymentDate ?? undefined)
        : invoice.paymentDate,
    amount: FormFieldConstants.IncomingInvoice.Details.Amount in values ? Math.abs(values.amount) : invoice.amount,
    tax: FormFieldConstants.IncomingInvoice.Details.Vat in values ? Math.abs(values.vat) : invoice.tax,
    oCR: FormFieldConstants.IncomingInvoice.Details.Ocr in values ? values.ocr : invoice.oCR,
    currency: FormFieldConstants.IncomingInvoice.Details.Currency in values ? values.currency : invoice.currency,
    isCredit: isCredit,
    isAlreadyPaid: isAlreadyPaid,
    dimensionList:
      FormFieldConstants.IncomingInvoice.Details.Dimensions in values ? values.dimensions : invoice.dimensionList,
    ...(FormFieldConstants.IncomingInvoice.Details.Periodisation in values
      ? PeriodisationRowsHelper.toInvoiceFields(values.periodisation, PeriodisationRowsHelper.invoiceMonth(invoiceDate))
      : {
          periodisationStart: invoice.periodisationStart,
          periodisationEnd: invoice.periodisationEnd,
          periodisationStartDate: invoice.periodisationStartDate,
          periodisationEndDate: invoice.periodisationEndDate,
          periodisationRows: invoice.periodisationRows,
        }),
    additionalInfoComment:
      FormFieldConstants.IncomingInvoice.Details.AdditionalInfoComment in values
        ? values.additionalInfoComment
        : invoice.additionalInfoComment,
    additionalInfoAttachments:
      FormFieldConstants.IncomingInvoice.Details.AdditionalInfoAttachments in values
        ? (values.additionalInfoAttachments as ApiModels.IncomingInvoiceAttachment[])
        : invoice.additionalInfoAttachments,
    paymentHandlingComment,
  };
  return updatedInvoice;
};
